import React, { useEffect, useDeferredValue, useState } from 'react';
import { Venue } from '@/types';
import {
  useVenuesPage,
  useVenueMutations,
  DataErrorBoundary,
  type VenueListParams
} from '@features/admin/data';
import { Edit2, Loader2, MapPin, Plus, Search, Trash2 } from 'lucide-react';
import { Pagination } from '@shared/components/common/Pagination';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';
import { getGoogleMapsUrl, getVenueMapUrl } from '@/lib/venueMaps';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface VenueListProps {
  canEdit: boolean;
  onEditDetails: (venue: Venue) => void;
  onEditSchedule: (venue: Venue) => void;
  onAddNew: () => void;
}

export const VenueList: React.FC<VenueListProps> = ({
  canEdit,
  onEditDetails,
  onEditSchedule,
  onAddNew
}) => {
  const venueMutations = useVenueMutations();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [deletingVenueId, setDeletingVenueId] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleDelete = async (venue: Venue) => {
    const confirmed = await showConfirmModal(
      `Are you sure you want to delete location "${venue.name}"? This action is permanent.`,
      { title: 'Delete Location', confirmText: 'Delete', cancelText: 'Cancel', isDestructive: true }
    );
    if (!confirmed) return;
    setDeletingVenueId(venue.id);
    try {
      await venueMutations.remove(venue.id);
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to delete location.'));
    } finally {
      setDeletingVenueId((current) => (current === venue.id ? null : current));
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h3 className="text-2xl font-bold text-slate-800">Venues & Locations</h3>
          <p className="text-sm text-slate-500 mt-1">
            Manage physical locations, opening hours, and holiday schedules.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search venues..."
              className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-56 bg-white text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canEdit && (
            <button
              onClick={onAddNew}
              className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-800 shadow-lg active:scale-95 transition-all"
            >
              <Plus className="w-5 h-5" /> Add New Venue
            </button>
          )}
        </div>
      </div>

      <DataErrorBoundary invalidateOnRetry="venues">
        <React.Suspense
          fallback={
            <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading venues...
            </div>
          }
        >
          <VenueListInner
            page={page}
            pageSize={pageSize}
            search={deferredSearch}
            canEdit={canEdit}
            deletingVenueId={deletingVenueId}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onEditDetails={onEditDetails}
            onEditSchedule={onEditSchedule}
            onDelete={(venue) => {
              void handleDelete(venue);
            }}
          />
        </React.Suspense>
      </DataErrorBoundary>
    </div>
  );
};

interface VenueListInnerProps {
  page: number;
  pageSize: number;
  search: string;
  canEdit: boolean;
  deletingVenueId: string | null;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEditDetails: (venue: Venue) => void;
  onEditSchedule: (venue: Venue) => void;
  onDelete: (venue: Venue) => void;
}

const VenueListInner: React.FC<VenueListInnerProps> = ({
  page,
  pageSize,
  search,
  canEdit,
  deletingVenueId,
  onPageChange,
  onPageSizeChange,
  onEditDetails,
  onEditSchedule,
  onDelete
}) => {
  const params: VenueListParams = { page, pageSize, q: search || undefined };
  const result = useVenuesPage(params);
  const venues = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {venues.length === 0 ? (
          <div className="col-span-full bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
            No venues found.
          </div>
        ) : (
          venues.map((venue) => {
            const mapUrl = getVenueMapUrl(venue, { width: 640, height: 360, zoom: 13 });
            const mapsLink = getGoogleMapsUrl(venue);
            return (
              <div
                key={venue.id}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden group hover:shadow-xl transition-all border-b-4 border-b-transparent hover:border-b-teal-500 flex flex-col"
              >
                <div className="h-44 bg-slate-100 relative overflow-hidden">
                  {mapUrl ? (
                    <img
                      src={mapUrl}
                      alt={`Map of ${venue.name}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <MapPin className="w-12 h-12 opacity-20" />
                    </div>
                  )}
                  {mapsLink && (
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${venue.name} in Google Maps`}
                      className="absolute inset-0 z-10 block"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 pointer-events-none" />
                  <div className="absolute bottom-3 left-3 text-white pointer-events-none">
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-80 mb-0.5">
                      Location ID: {venue.id}
                    </div>
                    <h4 className="font-bold text-lg leading-tight">{venue.name}</h4>
                  </div>
                  {canEdit && (
                    <div className="absolute top-3 right-3 z-20 flex gap-2 translate-y-[-10px] opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      <button
                        onClick={() => onEditDetails(venue)}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white text-slate-700 hover:text-blue-600 shadow-md"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(venue)}
                        disabled={deletingVenueId === venue.id}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg hover:bg-white text-slate-700 hover:text-red-600 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {deletingVenueId === venue.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 text-slate-600 text-sm mb-3">
                    <MapPin className="w-4 h-4 text-teal-500 shrink-0" />
                    <span className="font-medium">{venue.city}</span>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed mb-6">
                    {venue.address}
                  </p>
                  <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                        Status
                      </span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-xs font-bold text-slate-700 uppercase">
                          Operational
                        </span>
                      </div>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => onEditSchedule(venue)}
                        className="text-[10px] font-bold uppercase tracking-widest text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg transition-colors border border-teal-100"
                      >
                        Calendar & Hours
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};
